import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Spin, Alert, Dropdown, Menu } from 'antd';
import { 
  ReloadOutlined, 
  SettingOutlined, 
  DeleteOutlined, 
  MoreOutlined 
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import dashboardV2Service from '../../services/dashboardV2Service';
import TimeseriesWidget from './widgets/TimeseriesWidget';
import GaugeWidget from './widgets/GaugeWidget';
import NivelWidget from './widgets/NivelWidget';
import KPIWidget from './widgets/KPIWidget';
import './WidgetWrapper.scss';

const WidgetWrapper = ({ widget, onDelete, onEdit }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar datos del widget
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dashboardV2Service.getWidgetData(
        widget.widgetId,
        widget.tipo
      );
      setData(response.data);
    } catch (err) {
      console.error('Error loading widget data:', err);
      setError(err.response?.data?.error || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [widget.widgetId, widget.tipo]);

  // Auto-refresh y recargar cuando cambie la configuración
  useEffect(() => {
    loadData();

    const autoRefresh = widget.config?.autoRefresh;
    if (autoRefresh && autoRefresh > 0) {
      const interval = setInterval(loadData, autoRefresh * 1000);
      return () => clearInterval(interval);
    }
  }, [loadData, widget.config?.autoRefresh, widget.config]);

  // Pausar auto-refresh cuando el tab no está visible
  useEffect(() => {
    let interval;
    
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (interval) clearInterval(interval);
      } else {
        loadData();
        const autoRefresh = widget.config?.autoRefresh;
        if (autoRefresh && autoRefresh > 0) {
          interval = setInterval(loadData, autoRefresh * 1000);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (interval) clearInterval(interval);
    };
  }, [loadData, widget.config?.autoRefresh]);

  // Renderizar widget según tipo
  const renderWidget = () => {
    if (loading && !data) {
      return (
        <div className="widget-loading">
          <Spin size="large" tip="Cargando datos..." />
        </div>
      );
    }

    if (error) {
      return (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={loadData}>
              Reintentar
            </Button>
          }
        />
      );
    }

    if (!data) {
      return (
        <Alert
          message="Sin datos"
          description="No hay datos disponibles para este widget"
          type="info"
          showIcon
        />
      );
    }

    const widgetProps = {
      data,
      config: widget.config
    };

    switch (widget.tipo) {
      case 'timeseries':
        return <TimeseriesWidget {...widgetProps} />;
      case 'gauge':
        return <GaugeWidget {...widgetProps} />;
      case 'nivel':
        return <NivelWidget {...widgetProps} />;
      case 'kpi':
        return <KPIWidget {...widgetProps} />;
      default:
        return (
          <Alert
            message="Tipo no soportado"
            description={`El tipo de widget "${widget.tipo}" no está soportado`}
            type="warning"
            showIcon
          />
        );
    }
  };

  // Menú de acciones
  const menu = (
    <Menu>
      <Menu.Item key="edit" icon={<SettingOutlined />} onClick={() => onEdit(widget)}>
        Configurar
      </Menu.Item>
      <Menu.Item key="refresh" icon={<ReloadOutlined />} onClick={loadData}>
        Actualizar
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item 
        key="delete" 
        icon={<DeleteOutlined />} 
        danger
        onClick={() => onDelete(widget.widgetId)}
      >
        Eliminar
      </Menu.Item>
    </Menu>
  );

  return (
    <Card
      className={`widget-wrapper ${loading && data ? 'widget-refreshing' : ''}`}
      title={
        <div className="widget-header-content">
          <span className="widget-title">{widget.config?.titulo || 'Widget'}</span>
        </div>
      }
      extra={
        <Dropdown overlay={menu} trigger={['click']} placement="bottomRight">
          <Button 
            type="text" 
            icon={<MoreOutlined />} 
            size="small"
            className="widget-actions-btn"
          />
        </Dropdown>
      }
      bordered={false}
    >
      {loading && data && (
        <div className="widget-refresh-bar" />
      )}
      
      <div className="widget-content">
        {renderWidget()}
      </div>
      
      {data && widget.config?.autoRefresh > 0 && (
        <div className="widget-footer">
          <small className="text-muted">
            Se actualiza cada {widget.config.autoRefresh}s
          </small>
        </div>
      )}
    </Card>
  );
};

WidgetWrapper.propTypes = {
  widget: PropTypes.shape({
    widgetId: PropTypes.string.isRequired,
    tipo: PropTypes.oneOf(['timeseries', 'gauge', 'nivel', 'kpi']).isRequired,
    config: PropTypes.object.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
    w: PropTypes.number.isRequired,
    h: PropTypes.number.isRequired
  }).isRequired,
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired
};

export default WidgetWrapper;
